import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { PaxPicker } from "../widgets";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { StyleTypeContext } from "../../../umbilical_tools";
import { DateTime } from "luxon";
import { Formik, Form } from "formik";
import { DatepickerField } from "../../forms/inputGroup/helpers";
import jssVariables from "../../../jss_setup";
import Toggle from "../../forms/inputGroup/helpers/Toggle";
import { TrainStationsAutoCompleteField } from "../../forms/inputGroup/helpers/TrainStationsAutoComplete";
import { useTrainSearchFormHandle, validateForm } from "./helpers";
import { trainPaxOnlyMSF } from "../modals/RailPaxPickerModal";
import { SearchButton } from "../../common/Button";

const styles = createUseStyles({
  formContainer: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(3, max-content)",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.formContainer", {}),
  }),
  row1: (props) => ({
    ..._.get(props, "customStyles.row1", {}),
  }),
  row2: (props) => ({
    ..._.get(props, "customStyles.row2", {}),
  }),
  row3: (props) => ({
    ..._.get(props, "customStyles.row3", {}),
  }),
  mobileToggle: (props) => ({
    ..._.get(props, "customStyles.mobileToggle", {}),
  }),
  desktopToggle: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.desktopToggle", {}),
  }),
  searchBtnContr: (props) => ({
    display: "grid",
    justifyContent: "center",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    formContainer: (props) => ({
      padding: 0,
      ..._.get(props, "customStyles.formContainer.smallscreen", {}),
    }),
    row1: (props) => ({
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.row1.smallscreen", {}),
    }),
    row2: (props) => ({
      display: "grid",
      gridTemplateColumns: `repeat(${props.hasReturn ? 3 : 2}, 1fr)`,
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.row2.smallscreen", {}),
    }),
    row3: (props) => ({
      display: "grid",
      gridTemplateColumns: "repeat(2, max-content)",
      justifyContent: "space-between",
      alignItems: "end",
      ..._.get(props, "customStyles.row3.smallscreen", {}),
    }),
    mobileToggle: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.mobileToggle.smallscreen", {}),
    }),
    desktopToggle: (props) => ({
      display: "block",
      ..._.get(props, "customStyles.desktopToggle.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      padding: 0,
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
});
export const TrainSearchForm = ({ setShowRailPaxPickerModal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasReturn, setHasReturn] = useState(false);

  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "trainMainSearchFormStyles",
  });
  const classes = styles({ customStyles, hasReturn });

  const { search_form, onInitSearch, onClickPax, t } = useTrainSearchFormHandle(
    {
      setShowRailPaxPickerModal,
      setLoadingSearchBtn: setIsSubmitting,
    }
  );

  return (
    <Formik
      initialValues={{
        origin: {
          origin_meta: _.get(search_form, "origin_meta", {}),
        },
        destination: {
          destination_meta: _.get(search_form, "destination_meta", {}),
        },
        departDateTime: [search_form.departDateTime],
        returnDateTime: [search_form.returnDateTime],
        hasReturn: false,
      }}
      validate={async (values) => {
        if (values.hasReturn !== hasReturn) {
          setHasReturn(values.hasReturn);
        }
      }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);
          const hasErrors = await validateForm({
            ...search_form,
            ...values,
          });
          if (hasErrors) {
            setIsSubmitting(false);
            return;
          }
          await onInitSearch(values);
        }
      }}>
      <Form className={classes.formContainer}>
        <span className={classes.mobileToggle}>
          <Toggle name="hasReturn" label={t("return")} />
        </span>
        <div className={classes.row1}>
          <TrainStationsAutoCompleteField
            id="trainOrigin"
            name="origin"
            label="origin"
            placeholder={t("origin")}
            withErrors={false}
            customStyles={_.get(customStyles, "inputGroupField", {})}
          />
          <TrainStationsAutoCompleteField
            id="trainDestination"
            name="destination"
            label="destination"
            placeholder={t("destination")}
            withErrors={false}
            customStyles={_.get(customStyles, "inputGroupField", {})}
          />
        </div>
        <div className={classes.row2}>
          <DatepickerField
            label={t("departure")}
            name="departDateTime"
            selectsRange={false}
            dateFormat="MMM d, yyyy hh:mm aa"
            minDate={DateTime.now().toISODate()}
            maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
            placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
            withPortal={true}
            showTimeSelect={true}
            customStyles={_.get(customStyles, "inputGroupField", {})}
          />
          {hasReturn && (
            <DatepickerField
              label={t("return")}
              name="returnDateTime"
              selectsRange={false}
              dateFormat="MMM d, yyyy hh:mm aa"
              minDate={DateTime.now().toISODate()}
              maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
              placeholder={"Mon DD, YYYY - Mon DD, YYYY"}
              withPortal={true}
              showTimeSelect={true}
              customStyles={_.get(customStyles, "inputGroupField", {})}
            />
          )}
          <PaxPicker
            customStyles={_.get(customStyles, "inputGroupField", {})}
            adults={search_form.adults}
            adults_ages={search_form.adults_ages}
            childs={
              search_form.youths_ages.filter(
                (age) => age <= trainPaxOnlyMSF.children.to
              ).length
            }
            youths={
              search_form.youths_ages.filter(
                (age) => age >= trainPaxOnlyMSF.youths.from
              ).length
            }
            seniors={search_form.seniors}
            seniors_ages={search_form.seniors_ages}
            onClick={onClickPax}
            product_type="TRA"
          />
        </div>
        <div className={classes.row3}>
          <span className={classes.desktopToggle}>
            <Toggle name="hasReturn" label={t("return")} />
          </span>
          <div className={classes.searchBtnContr}>
            <SearchButton
              id="traSearchBtn"
              text={t("search")}
              loading={isSubmitting}
              customStyles={_.get(customStyles, "searchBtn", {})}
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};
TrainSearchForm.defaultProps = {
  customStyles: {},
  max_rooms_allowed: 0,
};
TrainSearchForm.propTypes = {
  customStyles: PropTypes.object.isRequired,
  max_rooms_allowed: PropTypes.number.isRequired,
  setShowRailPaxPickerModal: PropTypes.func.isRequired,
};
